/**
 * Shared AI-draft plumbing for the opt-in R-AI assist surfaces
 * (AI-SUMMARIES / AI-REC / AI-EXTRACTION + chat insight scan). Each fills an
 * existing *manual* surface with an editable draft — never auto-commits. They:
 *   - reuse the Analyst chat backend selector (`streamAgentReply` when
 *     `VITE_CHAT_BACKEND=agent`, else browser-direct `streamXaesReply`),
 *   - ground the draft on the same `<retrieved>` block the Analyst uses,
 *   - hand a human the streamed text to edit + Save/Add.
 *
 * `useAiDraft` is the common hook: it streams a draft, accumulates deltas,
 * and exposes status/cancel. The `build*Prompt` helpers assemble the
 * task-specific system + user prompt. Gated by `aiDraftConfigured()`
 * (= agent backend OR browser Anthropic key); keyless mock still exercises
 * the anthropic path when neither is on.
 */

import { useCallback, useRef, useState } from "react";
import { isAgentChatBackend, isClaudeConfigured } from "@/env";
import { useClaudeSettings } from "@/stores/useClaudeSettings";
import { WORKSHOPS, type Workshop } from "@/content/workshops";
import {
  FINDING_DOMAINS,
  FINDING_DOMAIN_DEFAULT,
  FINDING_SEVERITIES,
  FINDING_SEVERITY_DEFAULT,
  type FindingDomain,
  type FindingSeverity,
} from "@/lib/taxonomy";
import { streamAgentReply } from "./agent-api";
import { streamXaesReply } from "./vercel-ai";
import { buildSystemPrompt, type ChatScope } from "./context-builder";
import { buildRetrievedContext } from "./auto-retrieval";

/** Real transport available — agent BFF path or a browser Anthropic key.
 *  UIs still render + run (mock) when neither is on. */
export function aiDraftConfigured(): boolean {
  return isAgentChatBackend() || isClaudeConfigured();
}

export type AiDraftStatus = "idle" | "streaming" | "error";

export type UseAiDraftResult = {
  /** The accumulated draft text (updates live as deltas arrive). */
  text: string;
  setText: (value: string) => void;
  status: AiDraftStatus;
  error: string | null;
  configured: boolean;
  /** Stream a fresh draft from a system + user prompt. Resolves to the
   *  full text (also reflected in `text`). */
  run: (system: string, prompt: string) => Promise<string>;
  cancel: () => void;
  reset: () => void;
};

export function useAiDraft(): UseAiDraftResult {
  const model = useClaudeSettings((s) => s.model);
  const [text, setText] = useState("");
  const [status, setStatus] = useState<AiDraftStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const run = useCallback(
    async (system: string, prompt: string): Promise<string> => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setText("");
      setError(null);
      setStatus("streaming");
      try {
        const messages = [{ role: "user" as const, content: prompt }];
        // Same selector as Analyst chat + questionnaire clustering: agent
        // build keeps the key server-side; otherwise browser-direct AI SDK.
        const stream = isAgentChatBackend()
          ? streamAgentReply({ system, messages, signal: controller.signal })
          : streamXaesReply({
              system,
              messages,
              signal: controller.signal,
              model,
            });
        let acc = "";
        for await (const delta of stream) {
          if (controller.signal.aborted) break;
          acc += delta;
          setText(acc);
        }
        setStatus("idle");
        return acc;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Draft failed");
        setStatus("error");
        return "";
      }
    },
    [model],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    setStatus("idle");
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setText("");
    setError(null);
    setStatus("idle");
  }, []);

  return { text, setText, status, error, configured: aiDraftConfigured(), run, cancel, reset };
}

/* ── task-specific prompt assemblers ───────────────────────────────── */

/** AI-SUMMARIES — draft a previous-workshop summary grounded in its records. */
export async function buildSummaryPrompt(
  scope: ChatScope,
): Promise<{ system: string; prompt: string }> {
  const groundQuery = "decisions findings recommendations outcomes risks summary";
  const system = buildSystemPrompt({
    scope,
    resolvedReferences: [],
    autoContext: await buildRetrievedContext(scope, groundQuery, 12),
  });
  const prompt =
    "Write a concise 2–4 sentence summary of where this workshop left things, " +
    "suitable to carry forward into the next workshop. Ground it strictly in the " +
    "retrieved records above. Output ONLY the summary prose — no heading, no preamble.";
  return { system, prompt };
}

/** AI-REC — draft the body of a recommendation grounded in related records. */
export async function buildRecPrompt(
  scope: ChatScope,
  title: string,
): Promise<{ system: string; prompt: string }> {
  const system = buildSystemPrompt({
    scope,
    resolvedReferences: [],
    autoContext: await buildRetrievedContext(scope, title, 10),
  });
  const prompt =
    `Draft the body of this recommendation: "${title}". ` +
    "Ground it in the retrieved findings and opportunities above; explain the " +
    "rationale and the proposed course of action in 2–4 sentences. Output ONLY " +
    "the recommendation prose — no heading, no preamble.";
  return { system, prompt };
}

/** The two record kinds AI-EXTRACTION can propose from a transcript. */
export type ExtractionKind = "finding" | "opportunity";

/** AI-EXTRACTION — propose candidate findings *or* opportunities from a
 *  transcript. The model is told to emit one short title per line so the
 *  review UI can split it into add-able candidate rows. `kind` defaults to
 *  `"finding"`; the opportunity branch reframes the ask toward improvements /
 *  automation candidates worth pursuing. */
export function buildExtractionPrompt(
  scope: ChatScope,
  transcript: string,
  kind: ExtractionKind = "finding",
): { system: string; prompt: string } {
  const system = buildSystemPrompt({ scope, resolvedReferences: [] });
  const clipped = transcript.length > 6000 ? `${transcript.slice(0, 6000)}…` : transcript;
  const lines =
    kind === "opportunity"
      ? [
          "From the workshop transcript below, extract the distinct opportunities — " +
            "improvements, automation candidates, efficiencies, or value plays worth pursuing.",
          "Output ONE opportunity per line as a short title (≤ 12 words). No numbering, " +
            "no bullets, no preamble — just one opportunity title per line.",
        ]
      : [
          "From the workshop transcript below, extract the distinct findings — " +
            "observations, risks, gaps, or notable patterns worth recording.",
          "Output ONE finding per line as a short title (≤ 12 words). No numbering, " +
            "no bullets, no preamble — just one finding title per line.",
        ];
  const prompt = [...lines, "", "<transcript>", clipped, "</transcript>"].join("\n");
  return { system, prompt };
}

/** Split an extraction draft into candidate finding titles (one per line),
 *  dropping blanks, bullets/numbering, and the mock banner lines. */
export function parseCandidateTitles(draft: string): string[] {
  return draft
    .split(/\r?\n/)
    .map((l) => l.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, "").trim())
    .filter((l) => l.length > 0 && !l.startsWith("🤖") && !l.startsWith("_Mock"))
    .slice(0, 20);
}

/* ── Conversation-insight extraction (AI-CHAT-INSIGHTS) ─────────────── */

/**
 * Register kinds a chat-derived suggestion can land in.
 *
 * Taxonomy (pick the single best kind — do not dual-classify):
 *   - finding         — observation, risk, gap, or notable pattern
 *                       → Findings (Insight Library)
 *   - opportunity     — improvement / automation / value play (not yet committed)
 *                       → Opportunities (Insight Library)
 *   - recommendation  — proposed course of action with rationale (advisory)
 *                       → Recommendations (Insight Library)
 *   - decision        — a choice made or needing formal agreement
 *                       → Decisions (Decision Log)
 *   - action          — concrete next step someone should own/do
 *                       → Actions (Decision Log)
 *   - assumption      — working belief we are proceeding as if true
 *                       → Assumptions (Decision Log)
 *   - note            — session colour / park-for-later that isn't structured
 *                       → Facilitation notes
 *
 * Collision rules (encoded in the prompt): prefer `action` over
 * `recommendation` when it's a concrete next step; prefer `decision` when a
 * choice was made/needs agreement; prefer `assumption` for "proceeding as if";
 * prefer `note` only for ambient session colour that isn't a register item.
 * Richer than `ExtractionKind` (transcript surface: finding | opportunity).
 */
export type InsightKind =
  | "finding"
  | "opportunity"
  | "recommendation"
  | "decision"
  | "action"
  | "assumption"
  | "note";

const INSIGHT_KINDS: readonly InsightKind[] = [
  "finding",
  "opportunity",
  "recommendation",
  "decision",
  "action",
  "assumption",
  "note",
] as const;

function isInsightKind(value: unknown): value is InsightKind {
  return typeof value === "string" && (INSIGHT_KINDS as readonly string[]).includes(value);
}

/** One model-proposed insight, validated + coerced to the register taxonomy.
 *  `severity`/`domain` are populated for findings only; `workshopId` only when
 *  the extraction was scoped to Mission Control (the model picks the target).
 *  `title`/`detail` map to store fields on accept (assumption.statement/basis,
 *  note.body, decision.rationale, etc.). */
export type InsightCandidate = {
  kind: InsightKind;
  title: string;
  detail: string;
  severity?: FindingSeverity;
  domain?: FindingDomain;
  workshopId?: Workshop["id"];
};

/** Upper bound on insights surfaced per extraction pass — keeps a single
 *  reply from flooding the suggestion tray. Mirrors the transcript surface's
 *  20-row cap, scaled down for the chattier turn-by-turn cadence. */
const MAX_INSIGHTS = 6;

const KNOWN_WORKSHOP_IDS = new Set<string>(WORKSHOPS.map((w) => w.id));

/**
 * AI-CHAT-INSIGHTS — propose structured register suggestions from a chat
 * transcript. Unlike {@link buildExtractionPrompt} (one bare title per line,
 * single kind), this asks the model to classify across the full register
 * taxonomy and emit a strict JSON array so the suggestion tray can render
 * kind + detail and write to the right store on accept. For the Mission
 * Control scope (`"MC"`) the model also assigns each insight its single most
 * relevant workshop, since the registers are per-workshop and MC chat is
 * cross-programme.
 */
export function buildConversationInsightPrompt(
  scope: ChatScope,
  transcript: string,
): { system: string; prompt: string } {
  const system = buildSystemPrompt({ scope, resolvedReferences: [] });
  // Clip from the tail — the most recent turns carry the freshest insights.
  const clipped =
    transcript.length > 6000 ? `…${transcript.slice(-6000)}` : transcript;
  const isMc = scope === "MC";

  const fieldLines = [
    `- "kind": one of ${INSIGHT_KINDS.map((k) => `"${k}"`).join(", ")}.`,
    `- "title": a short title (≤ 12 words). For assumptions, this is the assumption statement.`,
    `- "detail": a 1–2 sentence explanation, grounded strictly in the conversation.` +
      ` For decisions this is the rationale; for assumptions the basis;` +
      ` for notes, optional colour/context (may be empty).`,
    `- "severity" (findings only): one of "info", "low", "medium", "high".`,
    `- "domain" (findings only): one of "process", "people", "technology", "governance", "data", "other".`,
  ];
  if (isMc) {
    const wsList = WORKSHOPS.map((w) => `    - ${w.id}: ${w.longTitle}`).join("\n");
    fieldLines.push(
      `- "workshopId": the single most relevant workshop id, chosen from:\n${wsList}`,
    );
  }

  const prompt = [
    "From the conversation transcript below, extract any concrete items worth",
    "recording in the workshop registers. Classify each as exactly one of:",
    "  - finding — an observation, risk, gap, or notable pattern;",
    "  - opportunity — a candidate improvement, automation, or value play (not yet committed);",
    "  - recommendation — an advisory proposed course of action with a rationale;",
    "  - decision — a choice that was made, or that needs formal agreement;",
    "  - action — a concrete next step someone should own and do;",
    "  - assumption — a working belief we are proceeding as if true;",
    "  - note — session colour or park-for-later that is not a structured register item.",
    "",
    "Collision rules (pick one kind only):",
    "  - Prefer action over recommendation when it is a concrete next step.",
    "  - Prefer decision when a choice was made or needs agreement.",
    "  - Prefer assumption for \"we are proceeding as if X\".",
    "  - Prefer note only for ambient session colour — do not dump structured items there.",
    "",
    "Only extract items that are clearly supported by the conversation — never invent.",
    "If the conversation holds nothing substantive yet, return an empty array.",
    "Leave owner / due / status empty — a human will fill those after Add.",
    "",
    "Output STRICT JSON only — a single array of objects, no prose, no markdown fences.",
    "Each object has these fields:",
    ...fieldLines,
    "",
    `Return at most ${MAX_INSIGHTS} of the strongest, most distinct items across all kinds.`,
    "",
    "<transcript>",
    clipped,
    "</transcript>",
  ].join("\n");

  return { system, prompt };
}

/** Pull the first JSON array out of a model draft, tolerating leading prose
 *  and ```json fences. Returns `null` when no parseable array is present
 *  (e.g. the keyless mock banner) so the caller surfaces zero suggestions. */
function extractJsonArray(draft: string): unknown {
  const unfenced = draft.replace(/```(?:json)?/gi, "");
  const start = unfenced.indexOf("[");
  const end = unfenced.lastIndexOf("]");
  if (start === -1 || end === -1 || end <= start) return null;
  try {
    return JSON.parse(unfenced.slice(start, end + 1));
  } catch {
    return null;
  }
}

/**
 * Validate + coerce a {@link buildConversationInsightPrompt} draft into
 * {@link InsightCandidate}s. Best-effort, same threat model as the store
 * sanitisers: unknown `kind` → drop the row; out-of-range severity/domain →
 * the neutral taxonomy default; title/detail clipped. When
 * `requireWorkshopId` is set (MC scope), a row without a *known* workshop id
 * is dropped rather than guessed; otherwise any `workshopId` is ignored.
 * Caps at {@link MAX_INSIGHTS}.
 */
export function parseInsightCandidates(
  draft: string,
  opts: { requireWorkshopId?: boolean } = {},
): InsightCandidate[] {
  const arr = extractJsonArray(draft);
  if (!Array.isArray(arr)) return [];

  const out: InsightCandidate[] = [];
  for (const raw of arr) {
    if (typeof raw !== "object" || raw === null) continue;
    const r = raw as Record<string, unknown>;

    const kind = r.kind;
    if (!isInsightKind(kind)) {
      continue;
    }
    const title = typeof r.title === "string" ? r.title.trim() : "";
    if (!title) continue;
    const detail = typeof r.detail === "string" ? r.detail.trim() : "";

    const candidate: InsightCandidate = {
      kind,
      title: title.slice(0, 140),
      detail: detail.slice(0, 600),
    };

    if (kind === "finding") {
      candidate.severity = FINDING_SEVERITIES.includes(r.severity as FindingSeverity)
        ? (r.severity as FindingSeverity)
        : FINDING_SEVERITY_DEFAULT;
      candidate.domain = FINDING_DOMAINS.includes(r.domain as FindingDomain)
        ? (r.domain as FindingDomain)
        : FINDING_DOMAIN_DEFAULT;
    }

    if (opts.requireWorkshopId) {
      const wsId = r.workshopId;
      if (typeof wsId !== "string" || !KNOWN_WORKSHOP_IDS.has(wsId)) continue;
      candidate.workshopId = wsId as Workshop["id"];
    }

    out.push(candidate);
    if (out.length >= MAX_INSIGHTS) break;
  }
  return out;
}
